import * as Sentry from '@sentry/react';
import { type ParsedLocation, redirect } from '@tanstack/react-router';
import { queryKeys, useGetOrganizationsColorMap, useGetOrganizationsForUserQuery } from '@vizit/api';
import { LoginError } from '@vizit/organisms';
import type { RouteContext } from '../../router-provider/types';

function beforeLoadAvatarColorValidation() {
  const storedIndex = localStorage.getItem('avatar_index');

  if (!storedIndex) {
    const newIndex = Math.floor(Math.random() * 5);
    localStorage.setItem('avatar_index', newIndex.toString());
  }
}
/* v8 ignore start */
async function beforeLoadOrganizationColorsValidation(context: RouteContext) {
  const { queryClient, auth } = context;

  if (!!auth?.userId && !!auth?.organizationId) {
    // Prefetch the organizations for the user
    try {
      await queryClient.prefetchQuery({
        queryKey: queryKeys.organizations.forUser({
          user_id: auth?.userId,
        }).queryKey,
        queryFn: () =>
          useGetOrganizationsForUserQuery.fetcher({
            // biome-ignore lint/style/noNonNullAssertion: We know the user is authenticated
            user_id: auth.userId!,
          })(),
        retry: false,
      });
    } catch (error) {
      // Silently fail - prefetch errors are non-critical
      Sentry.addBreadcrumb({ message: 'Failed to prefetch organizations for user.', level: 'warning' });
    }

    // Check if the organizations colors have been prefetched
    const hasOrganizationsColors = queryClient.getQueryState(
      queryKeys.organizations.colorMap({ user_id: auth?.userId }).queryKey,
    );

    if (!!auth?.userId && !!auth?.organizationId && !hasOrganizationsColors) {
      const organizations = queryClient.getQueryData(
        queryKeys.organizations.forUser({ user_id: auth?.userId }).queryKey,
      );
      if (organizations && Array.isArray(organizations)) {
        try {
          // Get the organizations colors
          await queryClient.prefetchQuery({
            queryKey: queryKeys.organizations.colorMap({
              user_id: auth?.userId,
            }).queryKey,
            queryFn: () =>
              useGetOrganizationsColorMap.fetcher(
                // biome-ignore lint/style/noNonNullAssertion: We know the user and organization are authenticated
                { user_id: auth?.userId! },
                queryClient,
              ),
            retry: false,
          });
        } catch (error) {
          // Silently fail - prefetch errors are non-critical
          Sentry.addBreadcrumb({ message: 'Failed to prefetch organizations colors for user.', level: 'warning' });
        }
      }
    }
  }
}
/* v8 ignore stop */

/**
 * Before loading the page, validates the authentication of the user.
 * If the user is not authenticated, it redirects to the login page.
 * This should only be used for pages that are private and cannot be accessed publicly.
 *
 * @param options - The before load context options of the route.
 * @example
 * ```tsx
 * const Route = createFileRoute('/example-private-page')({
 *   beforeLoad: ({ context }) => beforeLoadAuthValidation(context),
 * });
 * ```
 */
async function beforeLoadAuthenticatedValidation(context: RouteContext, location?: ParsedLocation) {
  const {
    isAuthenticated,
    organizationName,
    userEmail,
    defaultPortfolioId,
    userId,
    organizationId,
    isVizitUser,
    onReauthenticate,
  } = context?.auth ?? {};
  const { pendo, visitorId, visitorEmail, accountId, accountName, onIdentify } = context?.analytics ?? {};

  Sentry.addBreadcrumb({
    message: 'Route auth validation started',
    level: 'info',
    data: {
      is_authenticated: isAuthenticated,
      has_user_data: !!(organizationName && userEmail && defaultPortfolioId),
    },
  });

  // If the user is authenticated, but the user data is missing, we need to reauthenticate the user.
  if ((!organizationName || !userEmail || !defaultPortfolioId) && isAuthenticated && onReauthenticate) {
    Sentry.withScope((scope) => {
      if (userId) scope.setUser({ id: userId });

      Sentry.addBreadcrumb({
        message: 'Attempting to reauthenticate due to missing user data',
        level: 'info',
        data: {
          has_user_id: Boolean(userId),
          has_organization_name: Boolean(organizationName),
          has_user_email: Boolean(userEmail),
          has_default_portfolio_id: Boolean(defaultPortfolioId),
        },
      });
    });

    const result = await onReauthenticate();

    if (!result) {
      Sentry.addBreadcrumb({
        message: 'Redirecting to login after failed reauthentication',
        level: 'warning',
        data: { redirect_pathname: location?.pathname },
      });
      throw redirect({
        to: '/login',
        search: {
          error: LoginError.SessionExpired,
          redirect: location?.href && location?.href !== '/' ? location?.href : undefined,
        },
      });
    }
  }

  if (!isAuthenticated) {
    Sentry.addBreadcrumb({
      message: 'Redirecting to login - user not authenticated',
      level: 'info',
      data: { attempted_pathname: location?.pathname },
    });
    throw redirect({
      to: '/login',
    });
  }

  if (
    !!pendo &&
    !!visitorId &&
    !!userId &&
    (visitorId !== userId ||
      visitorEmail !== userEmail ||
      accountId !== organizationId ||
      accountName !== organizationName)
  ) {
    onIdentify?.(userId, organizationId, organizationName, userEmail);
  }

  if (isVizitUser) {
    await beforeLoadOrganizationColorsValidation(context);
  }

  beforeLoadAvatarColorValidation();
}

/**
 * Before loading the page, validates the user is a Vizit user.
 * If the user is not a Vizit user, it redirects to the home page.
 * This should only be used for pages that are private and cannot be accessed publicly.
 *
 * @param context - The before load context options of the route.
 * @param location - The location of the route.
 * @example
 * ```tsx
 * const Route = createFileRoute('/example-private-page')({
 *   beforeLoad: ({ context, location }) => beforeLoadVizitUserValidation(context, location),
 * });
 * ```
 */
async function beforeLoadVizitUserValidation(context: RouteContext, location?: ParsedLocation) {
  await beforeLoadAuthenticatedValidation(context, location);

  const { isVizitUser } = context?.auth ?? {};

  if (!isVizitUser) {
    throw redirect({ to: '/' });
  }
}

export { beforeLoadAuthenticatedValidation, beforeLoadVizitUserValidation };
